<!--
  Copyright (C) 2022 - Jeci SARL - https://jeci.fr

  This program is free software: you can redistribute it and/or modify
  it under the terms of the GNU Affero General Public License as
  published by the Free Software Foundation, either version 3 of the
  License, or (at your option) any later version.

  This program is distributed in the hope that it will be useful,
  but WITHOUT ANY WARRANTY; without even the implied warranty of
  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
  GNU Affero General Public License for more details.

  You should have received a copy of the GNU Affero General Public License
  along with this program.  If not, see https://www.gnu.org/licenses/agpl-3.0.html.
-->

<template>
  <div class="pdf-tools-page">
    <h1>
      {{ $t("pdfTools.title") }}
      <span v-if="currentFilename">— {{ currentFilename }}</span>
    </h1>

    <div v-if="isLoading" class="loading-container">
      <PrimeProgressSpinner />
      <p>{{ $t("pdfTools.loading") }}</p>
    </div>

    <div v-if="error" class="error-container">
      <PrimeMessage severity="error">{{ error }}</PrimeMessage>
    </div>

    <div v-if="!isLoading && !error" class="pdf-editor-layout">
      <div class="pdf-tools-sidebar">
        <div class="card">
          <h2>{{ $t("pdfTools.sidebar.actions") }}</h2>
          <div class="sidebar-form">
            <PrimeButton
              icon="ri-save-2-line"
              :label="$t('pdfTools.sidebar.saveNewVersion')"
              @click="saveChanges"
            />
            <PrimeButton
              severity="secondary"
              class="btn-mt"
              icon="ri-file-copy-2-line"
              :label="$t('pdfTools.sidebar.saveAs')"
              @click="openSaveAsPopup('saveAs')"
            />
            <PrimeDivider />
            <div class="form-field">
              <h3>{{ $t("pdfTools.sidebar.extractPages") }}</h3>
              <PrimeButton
                :disabled="selectedPages.length === 0"
                icon="ri-file-transfer-line"
                :label="$t('pdfTools.sidebar.extractAndSave')"
                @click="openSaveAsPopup('extract')"
              />
            </div>
            <div class="form-field form-field--spaced">
              <h3>{{ $t("pdfTools.sidebar.deletePages") }}</h3>
              <PrimeButton
                :disabled="
                  selectedPages.length === 0 ||
                  pages.length === selectedPages.length
                "
                severity="danger"
                icon="ri-delete-bin-line"
                :label="$t('pdfTools.sidebar.deleteSelectedPages')"
                @click="deleteSelectedPages"
              />
            </div>
            <div class="form-field form-field--spaced">
              <h3>{{ $t("pdfTools.sidebar.insertPdf") }}</h3>
              <div class="insert-buttons">
                <PrimeFileUpload
                  :auto="false"
                  :customUpload="false"
                  :showCancelButton="false"
                  :showUploadButton="false"
                  accept="application/pdf"
                  :choose-label="$t('pdfTools.sidebar.fromComputer')"
                  severity="secondary"
                  class="btn-full"
                  mode="basic"
                  name="pdfToInsert"
                  @select="onInsertPdfSelect"
                />
                <PrimeButton
                  severity="secondary"
                  icon="ri-cloud-line"
                  :label="$t('pdfTools.sidebar.fromPristy')"
                  @click="openInsertFromAlfrescoDialog"
                />
              </div>
            </div>
            <div class="form-field form-field--spaced">
              <h3>{{ $t("pdfTools.sidebar.addWatermark") }}</h3>
              <PrimeInputText
                v-model="watermarkText"
                :placeholder="$t('pdfTools.sidebar.watermarkTextPlaceholder')"
                type="text"
              />
              <div class="watermark-grid">
                <div>
                  <PrimeInputNumber
                    v-model="watermarkAngle"
                    :max="360"
                    :min="-360"
                    :placeholder="$t('pdfTools.sidebar.angle')"
                    suffix="°"
                  />
                </div>
                <div>
                  <PrimeInputNumber
                    v-model="watermarkRepetitions"
                    :max="10"
                    :min="1"
                    :placeholder="$t('pdfTools.sidebar.repetitions')"
                  />
                </div>
              </div>
              <PrimeButton
                :disabled="!watermarkText || selectedPages.length === 0"
                severity="secondary"
                class="btn-mt"
                icon="ri-paint-brush-line"
                :label="$t('pdfTools.sidebar.addWatermarkButton')"
                @click="addWatermark"
              />
            </div>
          </div>
        </div>
      </div>
      <div class="pdf-preview-container">
        <h2>{{ $t("pdfTools.pagePreview.title") }}</h2>
        <VueDraggableNext
          v-model="pages"
          class="page-grid"
          ghost-class="ghost"
          item-key="pageIndex"
          @end="onDragEnd"
        >
          <div
            v-for="element in pages"
            :key="element.pageIndex"
            class="page-grid-item"
          >
            <div
              :class="{ selected: isPageSelected(element) }"
              class="page-thumbnail"
              @click="togglePageSelection(element)"
              @dblclick.stop="openPageEditor(element)"
            >
              <div class="page-selection">
                <PrimeCheckbox binary :model-value="isPageSelected(element)" />
              </div>
              <div class="page-actions">
                <PrimeButton
                  text
                  rounded
                  icon="ri-clockwise-line"
                  :aria-label="$t('pdf.rotationRigth')"
                  @click.stop="rotatePage(element)"
                />
                <PrimeButton
                  :aria-label="
                    $t('pdfTools.pagePreview.openPagePreview', {
                      pageNumber: element.pageNumber,
                    })
                  "
                  text
                  rounded
                  icon="ri-edit-box-line"
                  @click.stop="openPageEditor(element)"
                />
              </div>
              <div class="thumbnail-content">
                <VuePDF
                  :annotations="false"
                  :page="element.pageNumber"
                  :pdf="pdf"
                  :scale="0.25"
                  :text="false"
                />
              </div>
              <div class="page-number">{{ element.pageNumber }}</div>
            </div>
          </div>
        </VueDraggableNext>
      </div>
    </div>

    <!-- Large preview / editing of a specific page -->
    <PrimeDialog
      v-model:visible="isPageEditorVisible"
      :breakpoints="{ '960px': '95vw', '640px': '100vw' }"
      dismissableMask
      modal
      class="dialog-page-editor"
      content-class="page-editor-dialog"
      @hide="closePageEditor"
    >
      <template #header>
        <div class="dialog-header">
          <i class="ri-file-fill" aria-hidden="true" />
          <span>
            {{
              $t("pdfTools.pageEditor.title", {
                pageNumber:
                  (selectedPageForEdit || selectedPage)?.pageNumber || "",
                filename: currentFilename,
              })
            }}
          </span>
        </div>
      </template>

      <div class="page-editor-toolbar">
        <PrimeButton
          text
          icon="ri-zoom-out-line"
          :aria-label="$t('pdf.dezoom')"
          @click="zoomOut"
        />
        <PrimeButton
          text
          icon="ri-zoom-in-line"
          :aria-label="$t('pdf.zoom')"
          @click="zoomIn"
        />
        <PrimeButton
          text
          :label="$t('pdfTools.pageEditor.fit')"
          @click="fitToWidth"
        />
        <span class="zoom-indicator">{{ Math.round(pageZoom * 100) }}%</span>
        <span class="spacer" />
        <PrimeButton
          text
          icon="ri-close-line"
          :label="$t('pdfTools.pageEditor.close')"
          @click="closePageEditor"
        />
      </div>
      <div class="editor-layout">
        <div class="tool-group">
          <h4>{{ $t("pdfTools.pageEditor.tools.actions") }}</h4>
          <PrimeButton
            text
            severity="secondary"
            :class="{ active: currentTool === EDITOR_TOOLS.SELECT }"
            icon="ri-cursor-line"
            :label="$t('pdfTools.pageEditor.tools.select')"
            @click="setTool(EDITOR_TOOLS.SELECT)"
          />
          <PrimeButton
            text
            severity="secondary"
            :class="{ active: currentTool === EDITOR_TOOLS.REDACT }"
            icon="ri-eraser-fill"
            :label="$t('pdfTools.pageEditor.tools.redact')"
            @click="setTool(EDITOR_TOOLS.REDACT)"
          />
          <PrimeButton
            text
            severity="secondary"
            :class="{ active: currentTool === EDITOR_TOOLS.HIGHLIGHT }"
            icon="ri-mark-pen-line"
            :label="$t('pdfTools.pageEditor.tools.highlight')"
            @click="setTool(EDITOR_TOOLS.HIGHLIGHT)"
          />
          <PrimeButton
            text
            severity="secondary"
            :class="{ active: currentTool === EDITOR_TOOLS.COMMENT }"
            icon="ri-sticky-note-line"
            :label="$t('pdfTools.pageEditor.tools.comment')"
            @click="setTool(EDITOR_TOOLS.COMMENT)"
          />
          <PrimeFileUpload
            severity="secondary"
            :class="{ active: currentTool === EDITOR_TOOLS.IMAGE }"
            accept="image/*"
            :choose-label="$t('pdfTools.pageEditor.tools.image')"
            mode="basic"
            name="image"
            @select="onOverlayImageSelect"
          />
          <PrimeButton
            text
            severity="secondary"
            :class="{ active: currentTool === EDITOR_TOOLS.SIGNATURE }"
            icon="ri-ball-pen-line"
            :label="$t('pdfTools.pageEditor.tools.signature')"
            @click="openSignaturePad"
          />
          <PrimeButton
            text
            severity="secondary"
            :class="{ active: currentTool === EDITOR_TOOLS.TEXT }"
            icon="ri-font-size"
            :label="$t('pdfTools.pageEditor.tools.text')"
            @click="setTool(EDITOR_TOOLS.TEXT)"
          />
          <div v-if="currentTool === EDITOR_TOOLS.TEXT" class="text-settings">
            <div class="form-field">
              <label class="form-label">{{
                $t("pdfTools.pageEditor.tools.font")
              }}</label>
              <PrimeSelect
                v-model="textFont"
                :options="textFontOptions"
                class="input-full"
                optionLabel="label"
                optionValue="value"
              />
            </div>
            <div class="form-field">
              <label class="form-label">{{
                $t("pdfTools.pageEditor.tools.size")
              }}</label>
              <PrimeInputNumber v-model="textSize" :max="144" :min="6" />
            </div>
            <div class="form-field">
              <label class="form-label">{{
                $t("pdfTools.pageEditor.tools.color")
              }}</label>
              <PrimeInputText
                v-model="textColor"
                class="input-full"
                placeholder="#000000"
              />
            </div>
          </div>
          <PrimeDivider />
          <PrimeButton
            :disabled="overlayItems.length === 0"
            severity="info"
            class="btn-full btn-mt-auto"
            icon="ri-check-line"
            :label="$t('pdfTools.pageEditor.apply')"
            @click="applyOverlayEdits"
          />
          <PrimeButton
            :disabled="overlayItems.length === 0"
            severity="warn"
            class="btn-full btn-mt-sm"
            icon="ri-arrow-go-back-line"
            :label="$t('pdfTools.pageEditor.cancel')"
            @click="undoOverlay"
          />
        </div>
        <div
          class="page-editor-canvas"
          @wheel.passive="syncOverlaySize"
          @scroll.passive="syncOverlaySize"
        >
          <div ref="pdfCanvasWrapper" class="pdf-canvas-wrapper">
            <VuePDF
              v-if="pdf && (selectedPageForEdit || selectedPage)"
              :annotations="false"
              :page="(selectedPageForEdit || selectedPage).pageNumber"
              :pdf="pdf"
              :scale="pageZoom"
              :text="false"
              class="pdf-canvas-absolute"
            />
            <canvas
              ref="overlayCanvas"
              class="overlay-canvas"
              @mousedown="onOverlayMouseDown"
              @mouseleave="onOverlayMouseLeave"
              @mousemove="onOverlayMouseMove"
              @mouseup="onOverlayMouseUp"
            />
          </div>
        </div>
      </div>

      <template #footer>
        <div class="editor-footer">
          <small>{{ $t("pdfTools.pageEditor.footerHint") }}</small>
        </div>
      </template>
    </PrimeDialog>

    <!-- Signature pad dialog -->
    <PrimeDialog
      v-model:visible="isSignaturePadVisible"
      modal
      class="dialog-signature-pad"
      @hide="onCloseSignaturePad"
    >
      <template #header>
        <div class="dialog-header">
          <i class="ri-ball-pen-line" aria-hidden="true" />
          <span>{{ $t("pdfTools.signaturePad.title") }}</span>
        </div>
      </template>
      <div class="signature-pad">
        <canvas
          ref="signaturePadCanvas"
          class="signature-canvas"
          @mousedown="sigDown"
          @mouseleave="sigUp"
          @mousemove="sigMove"
          @mouseup="sigUp"
          @touchstart.prevent="sigTouchDown"
          @touchmove.prevent="sigTouchMove"
          @touchend.prevent="sigUp"
        ></canvas>
      </div>
      <template #footer>
        <div class="dialog-footer-buttons">
          <PrimeButton
            text
            :label="$t('pdfTools.signaturePad.clear')"
            @click="sigClear"
          />
          <PrimeButton
            text
            :label="$t('pdfTools.signaturePad.use')"
            @click="sigUse"
          />
        </div>
      </template>
    </PrimeDialog>

    <SaveAsPopup
      :default-filename="defaultFilename"
      :visible="isSaveAsPopupVisible"
      :parent-folder-node="parentFolderNode"
      @close="isSaveAsPopupVisible = false"
      @save="handleSaveAs"
    />
    <!-- Dialog: Insert PDF from Alfresco -->
    <PrimeDialog
      :visible="isInsertFromAlfrescoVisible"
      modal
      :header="$t('pdfTools.insertFromAlfresco.title')"
      class="dialog-insert-pdf"
      @update:visible="isInsertFromAlfrescoVisible = false"
    >
      <PrimeTabs :value="0" @update:value="onTabChange">
        <PrimeTabList>
          <PrimeTab :value="0">{{
            $t("pdfTools.insertFromAlfresco.browse")
          }}</PrimeTab>
          <PrimeTab :value="1">{{
            $t("pdfTools.insertFromAlfresco.search")
          }}</PrimeTab>
          <PrimeTab :value="2">{{
            $t("pdfTools.insertFromAlfresco.favorites")
          }}</PrimeTab>
          <PrimeTab :value="3">{{
            $t("pdfTools.insertFromAlfresco.linkOrId")
          }}</PrimeTab>
        </PrimeTabList>
        <PrimeTabPanels>
          <PrimeTabPanel :value="0">
            <TreeExplorer
              showUserHome
              showFiles
              @selectNode="onNodeSelectedFromTree"
            />
          </PrimeTabPanel>
          <PrimeTabPanel :value="1">
            <div class="search-input-group">
              <PrimeInputText
                v-model="searchQuery"
                :placeholder="
                  $t('pdfTools.insertFromAlfresco.searchPlaceholder')
                "
                @keyup.enter="performSearch"
              />
              <PrimeButton
                icon="ri-search-line"
                :aria-label="$t('search')"
                @click="performSearch"
              />
            </div>
            <div v-if="searchLoading" class="inline-status">
              <PrimeProgressSpinner class="spinner-sm" strokeWidth="8" />
              <span>{{ $t("pdfTools.insertFromAlfresco.searching") }}</span>
            </div>
            <div
              v-else-if="!searchResults.length && searchPerformed"
              class="text-muted"
            >
              {{ $t("pdfTools.insertFromAlfresco.noPdfFound") }}
            </div>
            <div v-else-if="searchResults.length" class="favorites-layout">
              <div class="favorites-list">
                <div
                  v-for="item in searchResults"
                  :key="item.id"
                  class="favorite-item"
                  :class="{
                    'favorite-item-selected': selectedPdf?.id === item.id,
                  }"
                  @click="selectPdf(item)"
                >
                  <div class="item-content">
                    <i class="ri-file-pdf-line" aria-hidden="true" />
                    <span v-tooltip.top="item.name" class="text-ellipsis">{{
                      item.name
                    }}</span>
                  </div>
                </div>
              </div>
              <div class="favorites-preview">
                <div v-if="!selectedPdf" class="preview-placeholder">
                  <i class="ri-file-pdf-line preview-icon" aria-hidden="true" />
                  <p class="text-muted">
                    {{ $t("pdfTools.insertFromAlfresco.selectPdfToPreview") }}
                  </p>
                </div>
                <div v-else-if="previewLoading" class="preview-placeholder">
                  <PrimeProgressSpinner class="spinner-md" strokeWidth="6" />
                  <p class="text-muted">
                    {{ $t("pdfTools.insertFromAlfresco.loadingPreview") }}
                  </p>
                </div>
                <div v-else-if="previewPdf" class="preview-wrapper">
                  <div v-if="previewNumPages > 0" class="preview-controls">
                    <PrimeButton
                      icon="ri-arrow-left-s-line"
                      :aria-label="$t('pdf.previousPage')"
                      text
                      size="small"
                      :disabled="previewCurrentPage <= 1"
                      @click="previewPreviousPage"
                    />
                    <span class="page-info">
                      {{ previewCurrentPage }} / {{ previewNumPages }}
                    </span>
                    <PrimeButton
                      icon="ri-arrow-right-s-line"
                      :aria-label="$t('pdf.nextPage')"
                      text
                      size="small"
                      :disabled="previewCurrentPage >= previewNumPages"
                      @click="previewNextPage"
                    />
                  </div>
                  <div class="preview-content">
                    <VuePDF
                      :pdf="previewPdf"
                      :page="previewCurrentPage"
                      :scale="0.6"
                      :annotations="false"
                      :text="false"
                    />
                  </div>
                </div>
              </div>
            </div>
          </PrimeTabPanel>
          <PrimeTabPanel :value="2">
            <div v-if="favoritesLoading" class="inline-status">
              <PrimeProgressSpinner class="spinner-sm" strokeWidth="8" />
              <span>{{ $t("pdfTools.insertFromAlfresco.loading") }}</span>
            </div>
            <div v-else-if="!favoritePdfs.length" class="text-muted">
              {{ $t("pdfTools.insertFromAlfresco.noPdfFavorite") }}
            </div>
            <div v-else class="favorites-layout">
              <div class="favorites-list">
                <div
                  v-for="fav in favoritePdfs"
                  :key="fav.id"
                  class="favorite-item"
                  :class="{
                    'favorite-item-selected': selectedPdf?.id === fav.id,
                  }"
                  @click="selectPdf(fav)"
                >
                  <div class="item-content">
                    <i class="ri-file-pdf-line" aria-hidden="true" />
                    <span v-tooltip.top="fav.name" class="text-ellipsis">{{
                      fav.name
                    }}</span>
                  </div>
                </div>
              </div>
              <div class="favorites-preview">
                <div v-if="!selectedPdf" class="preview-placeholder">
                  <i class="ri-file-pdf-line preview-icon" aria-hidden="true" />
                  <p class="text-muted">
                    {{ $t("pdfTools.insertFromAlfresco.selectPdfToPreview") }}
                  </p>
                </div>
                <div v-else-if="previewLoading" class="preview-placeholder">
                  <PrimeProgressSpinner class="spinner-md" strokeWidth="6" />
                  <p class="text-muted">
                    {{ $t("pdfTools.insertFromAlfresco.loadingPreview") }}
                  </p>
                </div>
                <div v-else-if="previewPdf" class="preview-wrapper">
                  <div v-if="previewNumPages > 0" class="preview-controls">
                    <PrimeButton
                      icon="ri-arrow-left-s-line"
                      :aria-label="$t('pdf.previousPage')"
                      text
                      size="small"
                      :disabled="previewCurrentPage <= 1"
                      @click="previewPreviousPage"
                    />
                    <span class="page-info">
                      {{ previewCurrentPage }} / {{ previewNumPages }}
                    </span>
                    <PrimeButton
                      icon="ri-arrow-right-s-line"
                      :aria-label="$t('pdf.nextPage')"
                      text
                      size="small"
                      :disabled="previewCurrentPage >= previewNumPages"
                      @click="previewNextPage"
                    />
                  </div>
                  <div class="preview-content">
                    <VuePDF
                      :pdf="previewPdf"
                      :page="previewCurrentPage"
                      :scale="0.6"
                      :annotations="false"
                      :text="false"
                    />
                  </div>
                </div>
              </div>
            </div>
          </PrimeTabPanel>
          <PrimeTabPanel :value="3">
            <div class="sidebar-form">
              <div class="form-field">
                <label for="alfrescoNodeId">{{
                  $t("pdfTools.insertFromAlfresco.alfrescoIdUrl")
                }}</label>
                <PrimeInputText
                  id="alfrescoNodeId"
                  v-model="insertAlfrescoInput"
                  :placeholder="
                    $t('pdfTools.insertFromAlfresco.alfrescoIdUrlHint')
                  "
                />
                <small class="form-hint">
                  {{ $t("pdfTools.insertFromAlfresco.alfrescoIdUrlHint") }}
                </small>
              </div>
            </div>
          </PrimeTabPanel>
        </PrimeTabPanels>
      </PrimeTabs>
      <template #footer>
        <div class="insert-footer">
          <div class="insert-footer-info">
            <div v-if="selectedPdf" class="selected-info">
              <span class="selected-label">{{
                $t("pdfTools.insertFromAlfresco.selected")
              }}</span>
              <span v-tooltip.top="selectedPdf?.name" class="selected-name">
                {{ selectedPdf?.name }}
              </span>
            </div>
            <div class="error-msg" v-if="selectionErrorMsg">
              {{ selectionErrorMsg }}
            </div>
          </div>
          <div class="insert-footer-actions">
            <PrimeButton
              :label="$t('pdfTools.insertFromAlfresco.cancel')"
              text
              @click="isInsertFromAlfrescoVisible = false"
            />
            <PrimeButton
              :disabled="!canConfirmInsert"
              :label="$t('pdfTools.insertFromAlfresco.insert')"
              icon="ri-add-line"
              @click="confirmInsertFromAlfresco"
            />
          </div>
        </div>
      </template>
    </PrimeDialog>
  </div>
</template>

<script setup>
import { computed, onMounted, ref, watch } from "vue";
import {
  degrees,
  PDFDocument,
  rgb,
  RotationTypes,
  StandardFonts,
} from "pdf-lib";
import { usePDF, VuePDF } from "@tato30/vue-pdf";
import { buildPdfSourceRef } from "@/services/PdfConfigService.js";
import "@tato30/vue-pdf/style.css";
import { VueDraggableNext } from "vue-draggable-next";
import { useI18n } from "vue-i18n";
import PrimeProgressSpinner from "primevue/progressspinner";
import PrimeMessage from "primevue/message";
import PrimeButton from "primevue/button";
import PrimeCheckbox from "primevue/checkbox";
import PrimeInputText from "primevue/inputtext";
import PrimeInputNumber from "primevue/inputnumber";
import PrimeFileUpload from "primevue/fileupload";
import PrimeDivider from "primevue/divider";
import PrimeSelect from "primevue/select";
import PrimeDialog from "primevue/dialog";
import PrimeTabs from "primevue/tabs";
import PrimeTabList from "primevue/tablist";
import PrimeTab from "primevue/tab";
import PrimeTabPanels from "primevue/tabpanels";
import PrimeTabPanel from "primevue/tabpanel";
import SaveAsPopup from "@/components/pdfTools/SaveAsPopup.vue";
import alfrescoFileService from "@/services/AlfrescoFileService.js";
import alfrescoNodeService from "@/services/AlfrescoNodeService.js";
import { useFavoritesStore } from "@/stores/favorites.js";
import { useSearchStore } from "@/stores/search";
import TreeExplorer from "@/components/TreeExplorer.vue";

const { t } = useI18n();

const props = defineProps({
  toast: { type: Object, required: true },
  id: { type: String, required: true },
});

const pdfDoc = ref(null);
const originalPdfBytes = ref(null);
const pages = ref([]);
const isLoading = ref(true);
const error = ref(null);
const selectedPage = ref(null);
const selectedPages = ref([]);
// Page editor modal state
const isPageEditorVisible = ref(false);
const selectedPageForEdit = ref(null);
const pageZoom = ref(1);
// Overlay editing state for page editor
const EDITOR_TOOLS = {
  SELECT: "select",
  REDACT: "redact",
  HIGHLIGHT: "highlight",
  COMMENT: "comment",
  IMAGE: "image",
  SIGNATURE: "signature",
  TEXT: "text",
};
const currentTool = ref(EDITOR_TOOLS.SELECT); // select | redact | highlight | comment | image | signature | text
const overlayItems = ref([]); // {type, rect: {x,y,w,h} in PDF px at current render size, payload?}
const overlayImageFile = ref(null);
const signatureImageBytes = ref(null);
const pdfCanvasWrapper = ref(null);
const overlayCanvas = ref(null);
const isSignaturePadVisible = ref(false);
const signaturePadCanvas = ref(null);
let sigDrawing = false;
let sigLast = null;
let isDrawing = false;
let startPoint = null;
const watermarkText = ref("");
const watermarkAngle = ref(-45);
const watermarkRepetitions = ref(5);
// Text tool settings
const textFontOptions = [
  { label: "Helvetica", value: "Helvetica" },
  { label: "Times New Roman", value: "TimesRoman" },
  { label: "Courier", value: "Courier" },
];
const textFont = ref("Helvetica");
const textSize = ref(18);
const textColor = ref("#000000");
const parentId = ref(null);
const isSaveAsPopupVisible = ref(false);
const saveAsAction = ref(null); // 'saveAs' or 'extract'
const defaultFilename = ref("");
const currentFilename = ref("");
const currentNode = ref(null);
const parentFolderNode = ref(null);

// Insert from Alfresco dialog state
const isInsertFromAlfrescoVisible = ref(false);
const insertAlfrescoInput = ref("");
const selectionErrorMsg = ref("");

// Insert dialog enhanced state
const selectedPdf = ref(null); // stores selected node entry {id, name, content?.mimeType}
const canConfirmInsert = computed(() => {
  if (insertAlfrescoInput.value && parseNodeId(insertAlfrescoInput.value)) {
    return true;
  }
  return !!(selectedPdf.value && selectedPdf.value.id);
});

// Search state
const searchQuery = ref("");
const searchResults = ref([]);
const searchLoading = ref(false);
const searchPerformed = ref(false);

// Favorites state
const favoritePdfs = ref([]);
const favoritesLoading = ref(false);
const previewLoading = ref(false);
const previewPdfUrl = ref(null);
const { pdf: previewPdf } = usePDF(buildPdfSourceRef(previewPdfUrl));
const previewCurrentPage = ref(1);
const previewNumPages = ref(0);

const pdfUrl = ref(null);
const { pdf } = usePDF(buildPdfSourceRef(pdfUrl));

onMounted(() => {
  const nodeId = props.id;
  if (nodeId) {
    loadPdfFromAlfresco(nodeId);
  } else {
    error.value = t("pdfTools.loadError");
    isLoading.value = false;
  }
});

const isPageSelected = (page) => {
  return selectedPages.value.some((p) => p.pageIndex === page.pageIndex);
};

const togglePageSelection = (page) => {
  selectedPage.value = page;
  const index = selectedPages.value.findIndex(
    (p) => p.pageIndex === page.pageIndex,
  );
  if (index > -1) {
    selectedPages.value.splice(index, 1);
  } else {
    selectedPages.value.push(page);
  }
};

const loadPdfFromAlfresco = async (nodeId) => {
  isLoading.value = true;
  error.value = null;
  try {
    const node = await alfrescoNodeService.getNode(nodeId);
    currentNode.value = node;
    parentId.value = node.parentId;
    currentFilename.value =
      (node &&
        (node.name || (node.properties && node.properties["cm:name"]))) ||
      "";

    const contentUrl = alfrescoFileService.getContentUrl(nodeId);
    pdfUrl.value = contentUrl;

    const response = await fetch(contentUrl);
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    const arrayBuffer = await response.arrayBuffer();
    originalPdfBytes.value = new Uint8Array(arrayBuffer);

    await updatePageList(originalPdfBytes.value);
    if (pages.value.length > 0) {
      selectedPage.value = pages.value[0];
    }
  } catch (err) {
    console.error("Error loading PDF from Alfresco:", err);
    error.value = t("pdfTools.loadError");
  } finally {
    isLoading.value = false;
  }
};

const updatePageList = async (pdfBytes) => {
  pdfDoc.value = await PDFDocument.load(pdfBytes);
  const pageCount = pdfDoc.value.getPageCount();
  pages.value = Array.from({ length: pageCount }, (_, i) => ({
    pageIndex: i,
    pageNumber: i + 1,
  }));
};

/**
 * Saves the modified PDF document, updates the component's state, and refreshes the PDF preview.
 * This avoids code duplication across multiple modification functions.
 * @param {PDFDocument} doc The modified pdf-lib document object.
 * @param {object} [options]
 * @param {boolean} [options.updatePages=true] Whether to regenerate the page list (e.g., after add/remove/reorder).
 */
const _updatePdfAndRefreshState = async (doc, { updatePages = true } = {}) => {
  const newPdfBytes = await doc.save();
  originalPdfBytes.value = newPdfBytes;

  const blob = new Blob([newPdfBytes], { type: "application/pdf" });
  if (pdfUrl.value && pdfUrl.value.startsWith("blob:")) {
    URL.revokeObjectURL(pdfUrl.value);
  }
  pdfUrl.value = URL.createObjectURL(blob);

  if (updatePages) {
    await updatePageList(newPdfBytes);
  }
};

const onDragEnd = async () => {
  if (!originalPdfBytes.value) {
    return;
  }
  isLoading.value = true;
  try {
    const newPdfDoc = await PDFDocument.create();
    const pageIndices = pages.value.map((p) => p.pageIndex);

    const sourcePdf = await PDFDocument.load(originalPdfBytes.value);
    const copiedPages = await newPdfDoc.copyPages(sourcePdf, pageIndices);
    copiedPages.forEach((page) => newPdfDoc.addPage(page));

    await _updatePdfAndRefreshState(newPdfDoc);
  } catch (err) {
    console.error("Error reordering pages:", err);
    error.value = t("pdfTools.reorderError");
  } finally {
    isLoading.value = false;
  }
};

const rotatePage = async (pageElement) => {
  if (!originalPdfBytes.value) {
    return;
  }

  isLoading.value = true;
  try {
    const doc = await PDFDocument.load(originalPdfBytes.value);
    const page = doc.getPage(pageElement.pageIndex);
    const currentRotation = page.getRotation().angle;
    page.setRotation({
      type: RotationTypes.Degrees,
      angle: (currentRotation + 90) % 360,
    });

    // Page list does not need to be updated as the number of pages is unchanged
    await _updatePdfAndRefreshState(doc, { updatePages: false });
  } catch (err) {
    console.error("Error rotating page:", err);
    error.value = t("pdfTools.rotateError");
  } finally {
    isLoading.value = false;
  }
};

const extractPages = async () => {
  if (selectedPages.value.length === 0 || !originalPdfBytes.value) {
    return null;
  }
  const newPdfDoc = await PDFDocument.create();
  const sourcePdf = await PDFDocument.load(originalPdfBytes.value);
  const pageIndices = selectedPages.value
    .map((p) => p.pageIndex)
    .sort((a, b) => a - b);
  const copiedPages = await newPdfDoc.copyPages(sourcePdf, pageIndices);
  copiedPages.forEach((page) => newPdfDoc.addPage(page));
  return await newPdfDoc.save();
};

const deleteSelectedPages = async () => {
  if (!originalPdfBytes.value || selectedPages.value.length === 0) {
    return;
  }
  if (selectedPages.value.length === pages.value.length) {
    props.toast.add({
      severity: "warn",
      summary: t("pdfTools.common.warning"),
      detail: t("pdfTools.deleteAllPagesWarning"),
      life: 3000,
    });
    return;
  }
  isLoading.value = true;
  try {
    const src = await PDFDocument.load(originalPdfBytes.value);
    const total = src.getPageCount();
    const toDelete = new Set(selectedPages.value.map((p) => p.pageIndex));
    const keepIndices = [];
    for (let i = 0; i < total; i++) {
      if (!toDelete.has(i)) {
        keepIndices.push(i);
      }
    }
    const newDoc = await PDFDocument.create();
    const copied = await newDoc.copyPages(src, keepIndices);
    copied.forEach((p) => newDoc.addPage(p));

    await _updatePdfAndRefreshState(newDoc);
    selectedPages.value = [];

    props.toast.add({
      severity: "success",
      summary: t("pdfTools.common.deleted"),
      detail: t("pdfTools.pagesDeletedSuccess"),
      life: 3000,
    });
  } catch (err) {
    console.error("Error deleting pages:", err);
    error.value = t("pdfTools.deletePagesError");
  } finally {
    isLoading.value = false;
  }
};

const openInsertFromAlfrescoDialog = async () => {
  insertAlfrescoInput.value = "";
  selectedPdf.value = null;
  selectionErrorMsg.value = "";
  isInsertFromAlfrescoVisible.value = true;
};

const onTabChange = (tabValue) => {
  // Load favorites when switching to favorites tab (value 2: Browse=0, Search=1, Favorites=2)
  if (
    tabValue === 2 &&
    favoritePdfs.value.length === 0 &&
    !favoritesLoading.value
  ) {
    loadFavorites();
  }
};

const confirmInsertFromAlfresco = async () => {
  let nodeId = null;
  if (selectedPdf.value?.id) {
    nodeId = selectedPdf.value.id;
  } else if (insertAlfrescoInput.value) {
    nodeId = parseNodeId(insertAlfrescoInput.value);
  }
  if (!nodeId) {
    props.toast.add({
      severity: "warn",
      summary: t("pdfTools.common.warning"),
      detail: t("pdfTools.insertFromAlfresco.invalidInput"),
      life: 3000,
    });
    return;
  }
  await insertPdfFromAlfresco(nodeId);
  isInsertFromAlfrescoVisible.value = false;
};

const parseNodeId = (text) => {
  if (!text) {
    return null;
  }
  const trimmed = text.trim();
  // Direct ID (UUID-like)
  if (/^[0-9a-fA-F-]{20,}$/.test(trimmed)) {
    return trimmed;
  }
  // workspace://SpacesStore/<id>
  const ws = trimmed.match(/workspace:\/\/SpacesStore\/([0-9a-fA-F-]{20,})/);
  if (ws && ws[1]) {
    return ws[1];
  }
  // URLs like .../nodes/<id> or routes containing the id in path
  const inNodes = trimmed.match(
    /\b(nodes|preview|renditions|content)\/?([0-9a-fA-F-]{20,})\b/,
  );
  if (inNodes && inNodes[2]) {
    return inNodes[2];
  }
  // Hash routes e.g. .../#/pdf-tools/<id> or .../#/consultation-pdf/<id>
  const hash = trimmed.match(
    /#\/(?:pdf-tools|consultation-pdf|edit-collabora)\/([0-9a-fA-F-]{20,})/,
  );
  if (hash && hash[1]) {
    return hash[1];
  }
  // Fallback: try to find a 32+ hex segment
  const generic = trimmed.match(/([0-9a-fA-F]{20,})/);
  if (generic && generic[1]) {
    return generic[1];
  }
  return null;
};

const selectPdf = async (node) => {
  if (node?.content?.mimeType !== "application/pdf") {
    return;
  }
  selectedPdf.value = node;
  selectionErrorMsg.value = ""; // Clear any previous error message

  // Load preview and reset to first page
  if (node?.id) {
    previewLoading.value = true;
    previewCurrentPage.value = 1;
    previewNumPages.value = 0; // Reset while loading
    try {
      const contentUrl = alfrescoFileService.getContentUrl(node.id);
      // Revoke old preview URL if exists
      if (previewPdfUrl.value && previewPdfUrl.value.startsWith("blob:")) {
        URL.revokeObjectURL(previewPdfUrl.value);
      }
      previewPdfUrl.value = contentUrl;
    } catch (err) {
      console.error("Error loading PDF preview:", err);
      previewPdfUrl.value = null;
    } finally {
      previewLoading.value = false;
    }
  }
};

const previewNextPage = () => {
  if (previewCurrentPage.value < previewNumPages.value) {
    previewCurrentPage.value++;
  }
};

const previewPreviousPage = () => {
  if (previewCurrentPage.value > 1) {
    previewCurrentPage.value--;
  }
};

const onNodeSelectedFromTree = (nodeData) => {
  selectionErrorMsg.value = "";
  if (nodeData.isFolder) {
    selectedPdf.value = null;
    selectionErrorMsg.value = t(
      "pdfTools.insertFromAlfresco.folderSelectError",
    );
  } else if (nodeData.content?.mimeType !== "application/pdf") {
    selectedPdf.value = null;
    selectionErrorMsg.value = t(
      "pdfTools.insertFromAlfresco.fileTypeNotAllowedError",
    );
  } else {
    selectPdf(nodeData);
  }
};

const performSearch = async () => {
  searchPerformed.value = true;
  if (!searchQuery.value || searchQuery.value.trim().length < 2) {
    searchResults.value = [];
    return;
  }
  searchLoading.value = true;
  try {
    // Use search store for a simple query, then filter to PDFs
    const store = useSearchStore();
    await store.queryStore(searchQuery.value);
    const entries = store.entries.map((e) => e.entry || e); // store shape
    searchResults.value = entries.filter(
      (e) => e.isFile && e.content?.mimeType === "application/pdf",
    );
  } catch (e) {
    console.error("performSearch", e);
    searchResults.value = [];
  } finally {
    searchLoading.value = false;
  }
};

const loadFavorites = async () => {
  favoritesLoading.value = true;
  try {
    const favoritesStore = useFavoritesStore();
    await favoritesStore.loadFavorites();

    // Filter to keep only PDFs and extract the file object
    favoritePdfs.value = favoritesStore.fileFavorites
      .filter(
        (fav) => fav?.target?.file?.content?.mimeType === "application/pdf",
      )
      .map((fav) => fav.target.file);
  } catch (e) {
    console.error("loadFavorites error:", e);
    favoritePdfs.value = [];
  } finally {
    favoritesLoading.value = false;
  }
};

const insertPdfFromAlfresco = async (nodeId) => {
  if (!originalPdfBytes.value) {
    return;
  }
  isLoading.value = true;
  try {
    // Optionally verify node and ensure mimeType is PDF
    const node = await alfrescoNodeService.getNode(nodeId);
    if (node.content?.mimeType !== "application/pdf") {
      throw new Error(t("pdfTools.invalidNodeForInsert"));
    }
    const contentBlob = await alfrescoNodeService.getNodeContent(nodeId);
    const arrayBuffer = await contentBlob.arrayBuffer();
    const otherBytes = new Uint8Array(arrayBuffer);

    const current = await PDFDocument.load(originalPdfBytes.value);
    const other = await PDFDocument.load(otherBytes);
    const count = other.getPageCount();
    const importIndices = Array.from({ length: count }, (_, i) => i);
    const copied = await current.copyPages(other, importIndices);
    copied.forEach((p) => current.addPage(p));

    await _updatePdfAndRefreshState(current);
    selectedPages.value = [];

    props.toast.add({
      severity: "success",
      summary: t("pdfTools.common.success"),
      detail: t("pdfTools.insertFromPristySuccess", { count }),
      life: 3000,
    });
  } catch (err) {
    console.error("Error inserting from Alfresco:", err);
    error.value = err?.message || t("pdfTools.insertFromPristyError");
    props.toast.add({
      severity: "error",
      summary: t("pdfTools.common.error"),
      detail: error.value,
      life: 4000,
    });
  } finally {
    isLoading.value = false;
  }
};

const onInsertPdfSelect = async (e) => {
  const file = e.files?.[0];
  if (!file) {
    return;
  }
  await insertPdfFile(file);
};

const insertPdfFile = async (file) => {
  if (!originalPdfBytes.value) {
    return;
  }
  isLoading.value = true;
  try {
    const current = await PDFDocument.load(originalPdfBytes.value);
    const otherBytes = new Uint8Array(await file.arrayBuffer());
    const other = await PDFDocument.load(otherBytes);
    const count = other.getPageCount();
    const importIndices = Array.from({ length: count }, (_, i) => i);
    const copied = await current.copyPages(other, importIndices);
    copied.forEach((p) => current.addPage(p));

    await _updatePdfAndRefreshState(current);
    selectedPages.value = [];

    props.toast.add({
      severity: "success",
      summary: t("pdfTools.common.success"),
      detail: t("pdfTools.insertFromFileSuccess", { count }),
      life: 3000,
    });
  } catch (err) {
    console.error("Error inserting PDF:", err);
    error.value = t("pdfTools.insertError");
  } finally {
    isLoading.value = false;
  }
};

const addWatermark = async () => {
  if (
    !watermarkText.value ||
    selectedPages.value.length === 0 ||
    !originalPdfBytes.value
  ) {
    return;
  }

  isLoading.value = true;
  try {
    const doc = await PDFDocument.load(originalPdfBytes.value);
    const pageIndices = selectedPages.value.map((p) => p.pageIndex);

    for (const pageIndex of pageIndices) {
      const page = doc.getPage(pageIndex);
      const { width, height } = page.getSize();

      for (let i = 0; i < watermarkRepetitions.value; i++) {
        page.drawText(watermarkText.value, {
          x: (width / (watermarkRepetitions.value + 1)) * (i + 1) - 150,
          y: (height / (watermarkRepetitions.value + 1)) * (i + 1),
          size: 50,
          color: rgb(0.95, 0.1, 0.1),
          opacity: 0.5,
          rotate: degrees(watermarkAngle.value),
        });
      }
    }

    await _updatePdfAndRefreshState(doc, { updatePages: false });
  } catch (err) {
    console.error("Error adding watermark:", err);
    error.value = t("pdfTools.watermarkError");
  } finally {
    isLoading.value = false;
  }
};

const saveChanges = async () => {
  if (!originalPdfBytes.value) {
    return;
  }
  isLoading.value = true;
  try {
    const blob = new Blob([originalPdfBytes.value], {
      type: "application/pdf",
    });
    const file = new File([blob], "modified.pdf", { type: "application/pdf" });
    const opts = {
      majorVersion: true,
      comment: "File modified via Pristy Espaces PDF tools",
    };
    await alfrescoNodeService.updateContentNode(props.id, file, opts);
    props.toast.add({
      severity: "success",
      summary: t("pdfTools.common.success"),
      detail: t("pdfTools.saveSuccess"),
      life: 3000,
    });
  } catch (err) {
    console.error("Error saving changes:", err);
    props.toast.add({
      severity: "error",
      summary: t("pdfTools.common.error"),
      detail: t("pdfTools.saveError"),
      life: 3000,
    });
  } finally {
    isLoading.value = false;
  }
};

const openSaveAsPopup = async (action) => {
  saveAsAction.value = action;
  defaultFilename.value = t("pdfTools.saveAs.defaultFilename");
  if (currentNode.value && currentNode.value.parentId) {
    try {
      parentFolderNode.value = await alfrescoNodeService.getNode(
        currentNode.value.parentId,
      );
    } catch (e) {
      console.error("Could not fetch parent node for Save As dialog", e);
      parentFolderNode.value = null;
    }
  } else {
    parentFolderNode.value = null;
  }

  isSaveAsPopupVisible.value = true;
};

const handleSaveAs = async ({ filename, folderId }) => {
  isSaveAsPopupVisible.value = false;
  isLoading.value = true;
  try {
    let pdfBytesToSave;
    if (saveAsAction.value === "extract") {
      pdfBytesToSave = await extractPages();
    } else {
      pdfBytesToSave = originalPdfBytes.value;
    }

    if (!pdfBytesToSave) {
      throw new Error(t("pdfTools.noContentToSave"));
    }

    const blob = new Blob([pdfBytesToSave], { type: "application/pdf" });
    const file = new File([blob], filename, { type: "application/pdf" });

    if (!folderId) {
      throw new Error("No destination folder selected.");
    }

    await alfrescoFileService.uploadFile(folderId, {}, file);
    props.toast.add({
      severity: "success",
      summary: t("pdfTools.common.success"),
      detail: t("pdfTools.fileCreatedSuccess", { filename }),
      life: 3000,
    });
  } catch (err) {
    console.error(`Error during "Save As":`, err);
    const detail =
      err.status === 409
        ? t("pdfTools.saveAs.conflictError")
        : t("pdfTools.saveAsError");

    props.toast.add({
      severity: "error",
      summary: t("pdfTools.common.error"),
      detail,
      life: 4000,
    });
  } finally {
    isLoading.value = false;
  }
};
const openPageEditor = (page) => {
  selectedPageForEdit.value = page || selectedPage.value;
  isPageEditorVisible.value = true;
  pageZoom.value = 1;
  overlayItems.value = [];
  // Delay to size overlay
  setTimeout(syncOverlaySize, 200);
};

const closePageEditor = () => {
  isPageEditorVisible.value = false;
  selectedPageForEdit.value = null;
};

const zoomIn = () => {
  pageZoom.value = Math.min(4, pageZoom.value + 0.1);
};

const zoomOut = () => {
  pageZoom.value = Math.max(0.2, pageZoom.value - 0.1);
};

// Overlay helpers and tools
const setTool = (tool) => {
  if (tool === EDITOR_TOOLS.REDACT) {
    const ok = window.confirm(t("pdfTools.redactWarning"));
    if (ok) {
      currentTool.value = tool;
    }
  } else {
    currentTool.value = tool;
  }
};

const onOverlayImageSelect = (e) => {
  overlayImageFile.value = e.files?.[0] || null;
  if (overlayImageFile.value) {
    // Clear previous signature bytes so real image takes precedence
    signatureImageBytes.value = null;
    currentTool.value = EDITOR_TOOLS.IMAGE;
  }
};

const openSignaturePad = () => {
  isSignaturePadVisible.value = true;
};

const undoOverlay = () => {
  overlayItems.value.pop();
  drawOverlay();
};

// Ensure an image/signature overlay has a preview bitmap to draw on the overlay canvas
const ensureItemPreview = async (item) => {
  if (
    !item ||
    item.type !== EDITOR_TOOLS.IMAGE ||
    item._loading ||
    item._bitmap
  ) {
    return;
  }
  item._loading = true;
  try {
    let sourceBlob = null;
    if (item.payload?.file) {
      // File is already a Blob
      sourceBlob = item.payload.file;
    } else if (item.payload?.signature) {
      // Signature bytes are PNG by construction of the signature pad
      sourceBlob = new Blob([new Uint8Array(item.payload.signature)], {
        type: "image/png",
      });
    }
    if (sourceBlob) {
      item._bitmap = await createImageBitmap(sourceBlob);
      // Redraw to show the image once loaded
      drawOverlay();
    }
  } catch (e) {
    console.warn(t("pdfTools.imagePreviewUnavailable"), e);
  } finally {
    item._loading = false;
  }
};

const getPdfCanvas = () => {
  const wrapper = pdfCanvasWrapper.value;
  if (!wrapper) {
    return null;
  }
  const canvases = Array.from(wrapper.querySelectorAll("canvas"));
  if (canvases.length === 0) {
    return null;
  }
  // Choose the visible canvas with the largest area (VuePDF can create several)
  const visible = canvases.filter(
    (c) => c.offsetParent !== null && c.width > 0 && c.height > 0,
  );
  const list = visible.length ? visible : canvases;
  let best = list[0];
  let bestArea = 0;
  for (const c of list) {
    const r = c.getBoundingClientRect();
    const area = Math.max(0, r.width) * Math.max(0, r.height);
    if (area > bestArea) {
      best = c;
      bestArea = area;
    }
  }
  return best;
};

const syncOverlaySize = () => {
  const base = getPdfCanvas();
  if (!base || !overlayCanvas.value) {
    return;
  }
  const rect = base.getBoundingClientRect();
  const dpr = window.devicePixelRatio || 1;
  // overlay is absolutely positioned with inset:0 in CSS; avoid manual left/top to prevent offsets
  overlayCanvas.value.style.width = rect.width + "px";
  overlayCanvas.value.style.height = rect.height + "px";
  // Use DPR to keep overlay crisp
  overlayCanvas.value.width = Math.round(rect.width * dpr);
  overlayCanvas.value.height = Math.round(rect.height * dpr);
  drawOverlay();
};

const toCanvasPoint = (evt) => {
  const canvasEl = overlayCanvas.value;
  if (!canvasEl) {
    return { x: 0, y: 0 };
  }
  const rect = canvasEl.getBoundingClientRect();
  const dpr = window.devicePixelRatio || 1;
  return {
    x: (evt.clientX - rect.left) * dpr,
    y: (evt.clientY - rect.top) * dpr,
  };
};

let draggingIndex = null;
let dragStart = null;
let resizingIndex = null;
let resizeCorner = null;

const hitTest = (p) => {
  // iterate backwards so top-most wins
  for (let i = overlayItems.value.length - 1; i >= 0; i--) {
    const r = overlayItems.value[i].rect;
    if (p.x >= r.x && p.x <= r.x + r.w && p.y >= r.y && p.y <= r.y + r.h) {
      return i;
    }
  }
  return -1;
};

const hitTestHandle = (p) => {
  const dpr = window.devicePixelRatio || 1;
  const s = 6 * dpr;
  const half = s / 2;
  for (let i = overlayItems.value.length - 1; i >= 0; i--) {
    const it = overlayItems.value[i];
    if (it.type !== EDITOR_TOOLS.IMAGE && it.type !== EDITOR_TOOLS.TEXT) {
      continue;
    }
    const r = it.rect;
    const corners = [
      { key: "tl", x: r.x, y: r.y },
      { key: "tr", x: r.x + r.w, y: r.y },
      { key: "bl", x: r.x, y: r.y + r.h },
      { key: "br", x: r.x + r.w, y: r.y + r.h },
    ];
    for (const c of corners) {
      if (
        p.x >= c.x - half &&
        p.x <= c.x + half &&
        p.y >= c.y - half &&
        p.y <= c.y + half
      ) {
        return { idx: i, corner: c.key };
      }
    }
  }
  return null;
};

const onOverlayMouseDown = (e) => {
  if (
    currentTool.value === EDITOR_TOOLS.REDACT ||
    currentTool.value === EDITOR_TOOLS.HIGHLIGHT ||
    currentTool.value === EDITOR_TOOLS.TEXT
  ) {
    isDrawing = true;
    startPoint = toCanvasPoint(e);
  } else if (currentTool.value === EDITOR_TOOLS.COMMENT) {
    const p = toCanvasPoint(e);
    const text = window.prompt(t("pdfTools.pageEditor.commentPrompt"));
    if (text) {
      overlayItems.value.push({
        type: EDITOR_TOOLS.COMMENT,
        rect: { x: p.x, y: p.y, w: 24, h: 24 },
        payload: { text },
      });
      drawOverlay();
    }
  } else if (
    currentTool.value === EDITOR_TOOLS.IMAGE ||
    currentTool.value === EDITOR_TOOLS.SELECT ||
    currentTool.value === EDITOR_TOOLS.TEXT
  ) {
    // If clicking on an existing image item, start dragging instead of creating a new one
    const p = toCanvasPoint(e);
    // Check resize handles first
    const handleHit = hitTestHandle(p);
    if (handleHit) {
      resizingIndex = handleHit.idx;
      resizeCorner = handleHit.corner;
      dragStart = { p, rect: { ...overlayItems.value[handleHit.idx].rect } };
      if (overlayCanvas.value) {
        overlayCanvas.value.style.cursor = "nwse-resize";
      }
      return;
    }
    const idx = hitTest(p);
    if (
      idx !== -1 &&
      (overlayItems.value[idx].type === EDITOR_TOOLS.IMAGE ||
        overlayItems.value[idx].type === EDITOR_TOOLS.TEXT)
    ) {
      draggingIndex = idx;
      dragStart = { p, rect: { ...overlayItems.value[idx].rect } };
      if (overlayCanvas.value) {
        overlayCanvas.value.style.cursor = "move";
      }
      return;
    }
    if (currentTool.value === EDITOR_TOOLS.IMAGE) {
      // Only create a new image when the image tool is active and a source is provided
      if (!overlayImageFile.value && !signatureImageBytes.value) {
        return;
      }
      // creation of a new image rectangle
      const newItem = {
        type: EDITOR_TOOLS.IMAGE,
        rect: { x: p.x, y: p.y, w: 150, h: 100 },
        payload: {
          file: overlayImageFile.value,
          signature: signatureImageBytes.value,
        },
      };
      overlayItems.value.push(newItem);
      // kick off async preview loading
      ensureItemPreview(newItem);
      drawOverlay();
      // After adding an image, deselect the tool and clear sources to avoid re-adding the same image
      currentTool.value = EDITOR_TOOLS.SELECT;
      overlayImageFile.value = null;
      signatureImageBytes.value = null;
    }
  }
};

const onOverlayMouseMove = (e) => {
  // Resizing via handle
  if (resizingIndex !== null && resizeCorner) {
    const p = toCanvasPoint(e);
    const item = overlayItems.value[resizingIndex];
    if (item) {
      const r0 = dragStart.rect;
      let x = r0.x;
      let y = r0.y;
      let w = r0.w;
      let h = r0.h;
      const dx = p.x - dragStart.p.x;
      const dy = p.y - dragStart.p.y;
      if (resizeCorner === "br") {
        w = Math.max(10, r0.w + dx);
        h = Math.max(10, r0.h + dy);
      } else if (resizeCorner === "tr") {
        w = Math.max(10, r0.w + dx);
        h = Math.max(10, r0.h - dy);
        y = r0.y + dy;
      } else if (resizeCorner === "bl") {
        w = Math.max(10, r0.w - dx);
        x = r0.x + dx;
        h = Math.max(10, r0.h + dy);
      } else if (resizeCorner === "tl") {
        w = Math.max(10, r0.w - dx);
        x = r0.x + dx;
        h = Math.max(10, r0.h - dy);
        y = r0.y + dy;
      }
      item.rect.x = x;
      item.rect.y = y;
      item.rect.w = w;
      item.rect.h = h;
      drawOverlay();
    }
    return;
  }
  // Dragging existing item
  if (draggingIndex !== null) {
    const p = toCanvasPoint(e);
    const item = overlayItems.value[draggingIndex];
    if (item) {
      const dx = p.x - dragStart.p.x;
      const dy = p.y - dragStart.p.y;
      item.rect.x = dragStart.rect.x + dx;
      item.rect.y = dragStart.rect.y + dy;
      drawOverlay();
    }
    return;
  }
  if (!isDrawing || !startPoint) {
    return;
  }
  const p = toCanvasPoint(e);
  tempRect.value = {
    x: Math.min(startPoint.x, p.x),
    y: Math.min(startPoint.y, p.y),
    w: Math.abs(p.x - startPoint.x),
    h: Math.abs(p.y - startPoint.y),
  };
  drawOverlay();
};

const onOverlayMouseUp = () => {
  if (draggingIndex !== null || resizingIndex !== null) {
    draggingIndex = null;
    resizingIndex = null;
    resizeCorner = null;
    dragStart = null;
    if (overlayCanvas.value) {
      overlayCanvas.value.style.cursor = "crosshair";
    }
    drawOverlay();
    return;
  }
  if (isDrawing && tempRect.value) {
    if (currentTool.value === EDITOR_TOOLS.TEXT) {
      const entered = window.prompt(t("pdfTools.pageEditor.addTextPrompt"));
      if (entered && entered.trim().length > 0) {
        overlayItems.value.push({
          type: EDITOR_TOOLS.TEXT,
          rect: { ...tempRect.value },
          payload: {
            text: entered,
            font: textFont.value,
            size: textSize.value,
            color: textColor.value,
          },
        });
      }
    } else {
      overlayItems.value.push({
        type: currentTool.value,
        rect: { ...tempRect.value },
      });
    }
  }
  isDrawing = false;
  startPoint = null;
  tempRect.value = null;
  drawOverlay();
};

const onOverlayMouseLeave = () => {
  if (isDrawing) {
    isDrawing = false;
    startPoint = null;
    tempRect.value = null;
    drawOverlay();
  }
};

const tempRect = ref(null);

const drawOverlay = () => {
  const selectedIdx = draggingIndex;
  const canvasEl = overlayCanvas.value;
  if (!canvasEl) {
    return;
  }
  const ctx = canvasEl.getContext("2d");
  const dpr = window.devicePixelRatio || 1;
  ctx.clearRect(0, 0, canvasEl.width, canvasEl.height);

  const drawItem = (item, idx) => {
    if (item.type === EDITOR_TOOLS.REDACT) {
      ctx.fillStyle = "rgba(0,0,0,1)";
      ctx.fillRect(item.rect.x, item.rect.y, item.rect.w, item.rect.h);
      ctx.strokeStyle = "rgba(0,0,0,1)";
      ctx.strokeRect(item.rect.x, item.rect.y, item.rect.w, item.rect.h);
    } else if (item.type === EDITOR_TOOLS.HIGHLIGHT) {
      ctx.fillStyle = "rgba(255, 235, 59, 0.4)";
      ctx.fillRect(item.rect.x, item.rect.y, item.rect.w, item.rect.h);
      ctx.strokeStyle = "rgba(255, 193, 7, 0.8)";
      ctx.strokeRect(item.rect.x, item.rect.y, item.rect.w, item.rect.h);
    } else if (item.type === EDITOR_TOOLS.COMMENT) {
      drawCommentIcon(ctx, item.rect.x, item.rect.y, 20 * dpr);
    } else if (item.type === EDITOR_TOOLS.IMAGE) {
      // Try to ensure preview is available; this is async-safe
      ensureItemPreview(item);
      // Draw bitmap if loaded
      if (item._bitmap) {
        ctx.drawImage(
          item._bitmap,
          item.rect.x,
          item.rect.y,
          item.rect.w,
          item.rect.h,
        );
      }
      // Frame
      ctx.strokeStyle =
        idx === selectedIdx ? "#0ea5e9" : "rgba(33,150,243,0.9)";
      ctx.lineWidth = idx === selectedIdx ? 2 * dpr : 1 * dpr;
      ctx.strokeRect(item.rect.x, item.rect.y, item.rect.w, item.rect.h);
      if (idx === selectedIdx) {
        // draw simple handles
        ctx.fillStyle = "#0ea5e9";
        const s = 6 * dpr;
        const half = s / 2;
        const corners = [
          [item.rect.x, item.rect.y],
          [item.rect.x + item.rect.w, item.rect.y],
          [item.rect.x, item.rect.y + item.rect.h],
          [item.rect.x + item.rect.w, item.rect.y + item.rect.h],
        ];
        corners.forEach(([cx, cy]) => {
          ctx.fillRect(cx - half, cy - half, s, s);
        });
      }
    } else if (item.type === EDITOR_TOOLS.TEXT) {
      const payload = item.payload || {};
      const fontPx = Math.max(8, payload.size || 18);
      ctx.save();
      // background for readability
      ctx.fillStyle = "rgba(255,255,255,0.35)";
      ctx.fillRect(item.rect.x, item.rect.y, item.rect.w, item.rect.h);
      // text
      ctx.fillStyle = payload.color || "#000";
      ctx.font = `${fontPx}px ${payload.font || "Helvetica"}`;
      ctx.textBaseline = "top";
      const padding = 4 * dpr;
      const maxWidth = Math.max(0, item.rect.w - 2 * padding);
      const lines = wrapCanvasText(ctx, String(payload.text || ""), maxWidth);
      let ty = item.rect.y + padding;
      for (const line of lines) {
        ctx.fillText(line, item.rect.x + padding, ty);
        ty += fontPx * 1.2;
        if (ty > item.rect.y + item.rect.h - padding) {
          break;
        }
      }
      // frame + handles
      ctx.strokeStyle =
        idx === selectedIdx ? "#0ea5e9" : "rgba(33,150,243,0.9)";
      ctx.lineWidth = idx === selectedIdx ? 2 * dpr : 1 * dpr;
      ctx.strokeRect(item.rect.x, item.rect.y, item.rect.w, item.rect.h);
      if (idx === selectedIdx) {
        ctx.fillStyle = "#0ea5e9";
        const s = 6 * dpr;
        const half = s / 2;
        const corners = [
          [item.rect.x, item.rect.y],
          [item.rect.x + item.rect.w, item.rect.y],
          [item.rect.x, item.rect.y + item.rect.h],
          [item.rect.x + item.rect.w, item.rect.y + item.rect.h],
        ];
        corners.forEach(([cx, cy]) => {
          ctx.fillRect(cx - half, cy - half, s, s);
        });
      }
      ctx.restore();
    }
  };

  overlayItems.value.forEach(drawItem);
  if (tempRect.value) {
    const r = tempRect.value;
    ctx.setLineDash([6 * dpr, 4 * dpr]);
    ctx.strokeStyle =
      currentTool.value === EDITOR_TOOLS.REDACT ? "black" : "goldenrod";
    ctx.strokeRect(r.x, r.y, r.w, r.h);
    ctx.setLineDash([]);
  }
};

const wrapCanvasText = (ctx, text, maxWidth) => {
  const words = text.split(/\s+/);
  const lines = [];
  let line = "";
  for (const w of words) {
    const test = line ? line + " " + w : w;
    if (ctx.measureText(test).width <= maxWidth || line === "") {
      line = test;
    } else {
      lines.push(line);
      line = w;
    }
  }
  if (line) {
    lines.push(line);
  }
  return lines;
};

const hexToRgb01 = (hex) => {
  const { r, g, b } = parseHexColor(hex);
  return { r, g, b };
};

const parseHexColor = (hex) => {
  let h = (hex || "").toString().trim();
  if (h.startsWith("#")) {
    h = h.slice(1);
  }
  if (h.length === 3) {
    h = h
      .split("")
      .map((c) => c + c)
      .join("");
  }
  const num = parseInt(h || "000000", 16);
  const r = ((num >> 16) & 255) / 255;
  const g = ((num >> 8) & 255) / 255;
  const b = (num & 255) / 255;
  return { r, g, b };
};

const wrapPdfText = (font, text, size, maxWidth) => {
  const words = String(text).split(/\s+/);
  const lines = [];
  let line = "";
  for (const w of words) {
    const test = line ? line + " " + w : w;
    const width = font.widthOfTextAtSize(test, size);
    if (width <= maxWidth || line === "") {
      line = test;
    } else {
      lines.push(line);
      line = w;
    }
  }
  if (line) {
    lines.push(line);
  }
  return lines;
};

const drawCommentIcon = (ctx, x, y, size) => {
  ctx.save();
  ctx.translate(x, y);
  ctx.fillStyle = "#FFEB3B";
  ctx.strokeStyle = "#FBC02D";
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.rect(0, 0, size, size);
  ctx.fill();
  ctx.stroke();
  ctx.beginPath();
  ctx.moveTo(size * 0.6, 0);
  ctx.lineTo(size, size * 0.4);
  ctx.lineTo(size * 0.6, size * 0.4);
  ctx.closePath();
  ctx.fillStyle = "#FDD835";
  ctx.fill();
  ctx.restore();
};

const applyOverlayEdits = async () => {
  if (!originalPdfBytes.value || !selectedPageForEdit.value) {
    return;
  }
  isLoading.value = true;
  try {
    const doc = await PDFDocument.load(originalPdfBytes.value);
    const originalIndex = selectedPageForEdit.value.pageIndex;
    let page = doc.getPage(originalIndex);
    const { width: pdfW, height: pdfH } = page.getSize();

    const base = getPdfCanvas();
    if (!base) {
      throw new Error(t("pdfTools.pdfRenderCanvasNotFound"));
    }

    // If redaction is present, ask for confirmation then flatten the page to an image
    const hasRedact = overlayItems.value.some(
      (it) => it.type === EDITOR_TOOLS.REDACT,
    );
    if (hasRedact) {
      const ok = window.confirm(t("pdfTools.redactFlattenConfirm"));
      if (!ok) {
        isLoading.value = false;
        return;
      }
      // Rasterize the page at a higher resolution for a sharp redaction
      // A temporary canvas is created by multiplying the resolution based on the PDF and the current zoom
      const scale = Math.max(
        2,
        Math.ceil(
          base.width / base.getBoundingClientRect().width ||
            window.devicePixelRatio ||
            1,
        ),
      ); // at least x2
      const hiCanvas = document.createElement("canvas");
      // Size proportionally to the PDF page to limit distortion
      const ratio = base.width / base.height;
      // Use the base canvas size but upsampled
      hiCanvas.width = Math.round(base.width * scale);
      hiCanvas.height = Math.round(base.height * scale);
      const hctx = hiCanvas.getContext("2d");
      // Draw the visible canvas on the high-definition canvas, avoiding blur
      hctx.imageSmoothingEnabled = false;
      hctx.drawImage(
        base,
        0,
        0,
        base.width,
        base.height,
        0,
        0,
        hiCanvas.width,
        hiCanvas.height,
      );
      // Fetching from a data URL is a local operation, not a remote API call, so it doesn't violate the API service convention.
      const dataUrl = hiCanvas.toDataURL("image/jpeg", 0.5); // Medium quality JPG to reduce page weight
      const pngBytes = await (await fetch(dataUrl)).arrayBuffer();
      const bgImg = await doc.embedJpg(pngBytes);
      const newPage = doc.insertPage(originalIndex + 1, [pdfW, pdfH]);
      newPage.drawImage(bgImg, { x: 0, y: 0, width: pdfW, height: pdfH });
      // All overlays will be drawn on this new page
      page = newPage;
      // The old page will be deleted after applying the overlays
    }

    // VuePDF canvas measurements
    const baseRect = base.getBoundingClientRect(); // CSS px
    const baseW = base.width; // canvas pixels
    const baseH = base.height;

    // DPR used for the overlay
    const dpr = window.devicePixelRatio || 1;

    // Convert each item: overlay (px * dpr) -> CSS px -> base canvas px -> PDF coordinates
    const cssToBaseX = baseW / baseRect.width;
    const cssToBaseY = baseH / baseRect.height;

    const baseToPdfX = pdfW / baseW;
    const baseToPdfY = pdfH / baseH;

    for (const item of overlayItems.value) {
      // overlay rect is in overlayCanvas px (so already multiplied by dpr)
      const cssX = item.rect.x / dpr;
      const cssY = item.rect.y / dpr;
      const cssW = item.rect.w / dpr;
      const cssH = item.rect.h / dpr;

      const baseX = cssX * cssToBaseX;
      const baseY = cssY * cssToBaseY;
      const baseWRect = cssW * cssToBaseX;
      const baseHRect = cssH * cssToBaseY;

      const w = baseWRect * baseToPdfX;
      const h = baseHRect * baseToPdfY;
      const x = baseX * baseToPdfX;
      // Y inversion: top origin (canvas) -> bottom (PDF)
      const y = pdfH - (baseY + baseHRect) * baseToPdfY;

      if (item.type === EDITOR_TOOLS.REDACT) {
        page.drawRectangle({ x, y, width: w, height: h, color: rgb(0, 0, 0) });
      } else if (item.type === EDITOR_TOOLS.HIGHLIGHT) {
        page.drawRectangle({
          x,
          y,
          width: w,
          height: h,
          color: rgb(1, 0.92, 0.23),
          opacity: 0.4,
        });
      } else if (item.type === EDITOR_TOOLS.COMMENT) {
        page.drawRectangle({
          x,
          y,
          width: Math.max(14, w),
          height: Math.max(14, h),
          color: rgb(1, 0.92, 0.23),
          opacity: 0.9,
        });
        if (item.payload?.text) {
          const safe = String(item.payload.text).slice(0, 200);
          page.drawText(`Note: ${safe}`, {
            x: x + 2,
            y: y + h + 2,
            size: 8,
            color: rgb(0.2, 0.2, 0.2),
          });
        }
      } else if (item.type === EDITOR_TOOLS.IMAGE) {
        let bytes = null;
        if (item.payload?.file) {
          bytes = await item.payload.file.arrayBuffer();
        } else if (item.payload?.signature) {
          bytes = item.payload.signature;
        }
        if (bytes) {
          let img;
          try {
            img = await doc.embedPng(bytes);
          } catch (_) {
            img = await doc.embedJpg(bytes);
          }
          page.drawImage(img, { x, y, width: w, height: h, opacity: 0.98 });
        }
      } else if (item.type === EDITOR_TOOLS.TEXT) {
        const payload = item.payload || {};
        const fontName =
          payload.font === "TimesRoman"
            ? StandardFonts.TimesRoman
            : payload.font === "Courier"
              ? StandardFonts.Courier
              : StandardFonts.Helvetica;
        const font = await doc.embedFont(fontName);
        const size = Math.max(6, Number(payload.size) || 18);
        const {
          r: rc,
          g: gc,
          b: bc,
        } = parseHexColor(payload.color || "#000000");
        const pad = 2; // small padding in PDF units
        const maxLineWidth = Math.max(1, w - 2 * pad);
        const lines = wrapPdfText(
          font,
          String(payload.text || ""),
          size,
          maxLineWidth,
        );
        let ty = y + h - size - pad; // PDF y is bottom baseline; start from top within box
        for (const line of lines) {
          page.drawText(line, {
            x: x + pad,
            y: ty,
            size,
            color: rgb(rc, gc, bc),
          });
          ty -= size * 1.2;
          if (ty < y) {
            break;
          }
        }
      }
    }

    const needsPageListUpdate = overlayItems.value.some(
      (it) => it.type === EDITOR_TOOLS.REDACT,
    );
    // If a replacement page was inserted for redaction, remove the old page
    if (needsPageListUpdate) {
      doc.removePage(originalIndex);
    }

    await _updatePdfAndRefreshState(doc, { updatePages: needsPageListUpdate });

    overlayItems.value = [];
    props.toast.add({
      severity: "success",
      summary: t("pdfTools.common.success"),
      detail: t("pdfTools.pageEditor.applySuccess"),
      life: 2000,
    });
  } catch (err) {
    console.error("Error applying overlay edits:", err);
    props.toast.add({
      severity: "error",
      summary: t("pdfTools.common.error"),
      detail: t("pdfTools.pageEditor.applyError"),
      life: 3000,
    });
  } finally {
    isLoading.value = false;
  }
};

// Recompute overlay when zoom or page changes
watch(pageZoom, () => setTimeout(syncOverlaySize, 0));
watch(selectedPageForEdit, () => setTimeout(syncOverlaySize, 0));
// Recompute on window resize to keep alignment
if (typeof window !== "undefined") {
  window.addEventListener("resize", () => setTimeout(syncOverlaySize, 0));
}

const fitToWidth = () => {
  // Heuristic: set zoom to fit typical A4 in dialog width (~80vw). Keep simple here.
  pageZoom.value = 1;
  setTimeout(syncOverlaySize, 0);
};

// Signature pad logic
const initSignaturePadSize = () => {
  const canvas = signaturePadCanvas.value;
  if (!canvas) {
    return;
  }
  const dpr = window.devicePixelRatio || 1;
  const width = 440;
  const height = 180;
  canvas.style.width = width + "px";
  canvas.style.height = height + "px";
  canvas.width = Math.round(width * dpr);
  canvas.height = Math.round(height * dpr);
  const ctx = canvas.getContext("2d");
  ctx.fillStyle = "#fff";
  ctx.fillRect(0, 0, canvas.width, canvas.height);
};

const onCloseSignaturePad = () => {
  sigClear();
  isSignaturePadVisible.value = false;
};

const sigDown = (e) => {
  sigDrawing = true;
  sigLast = getSigPoint(e);
};
const sigMove = (e) => {
  if (!sigDrawing) {
    return;
  }
  const p = getSigPoint(e);
  drawSigLine(sigLast, p);
  sigLast = p;
};
const sigTouchDown = (e) => sigDown(e.touches[0]);
const sigTouchMove = (e) => sigMove(e.touches[0]);
const sigUp = () => {
  sigDrawing = false;
  sigLast = null;
};

const getSigPoint = (evt) => {
  const canvas = signaturePadCanvas.value;
  const rect = canvas.getBoundingClientRect();
  const dpr = window.devicePixelRatio || 1;
  const clientX = evt.clientX ?? evt.pageX;
  const clientY = evt.clientY ?? evt.pageY;
  return { x: (clientX - rect.left) * dpr, y: (clientY - rect.top) * dpr };
};

const drawSigLine = (a, b) => {
  const canvas = signaturePadCanvas.value;
  const ctx = canvas.getContext("2d");
  ctx.strokeStyle = "#111827";
  ctx.lineWidth = 3 * (window.devicePixelRatio || 1);
  ctx.lineCap = "round";
  ctx.beginPath();
  ctx.moveTo(a.x, a.y);
  ctx.lineTo(b.x, b.y);
  ctx.stroke();
};

const sigClear = () => {
  const canvas = signaturePadCanvas.value;
  if (!canvas) {
    return;
  }
  const ctx = canvas.getContext("2d");
  ctx.fillStyle = "#fff";
  ctx.fillRect(0, 0, canvas.width, canvas.height);
};

const sigUse = () => {
  const canvas = signaturePadCanvas.value;
  if (!canvas) {
    return;
  }
  canvas.toBlob(async (blob) => {
    signatureImageBytes.value = await blob.arrayBuffer();
    currentTool.value = EDITOR_TOOLS.IMAGE;
    isSignaturePadVisible.value = false;
  });
};

watch(isSignaturePadVisible, (v) => {
  if (v) {
    setTimeout(initSignaturePadSize, 0);
  }
});

// Watcher to get numPages when PDF loads
watch(previewPdf, async (newVal) => {
  if (!newVal) {
    previewNumPages.value = 0;
    return;
  }

  try {
    // Wait for the PDF document to load
    const pdfDoc = await newVal;
    previewNumPages.value = pdfDoc.numPages;
  } catch (err) {
    console.error("Error loading preview PDF:", err);
    previewNumPages.value = 0;
  }
});
</script>

<style scoped>
.pdf-tools-page {
  padding: 2rem;
}

.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 50vh;
}

.pdf-editor-layout {
  display: flex;
  gap: 2rem;
}

.pdf-tools-sidebar {
  width: 350px;
  flex-shrink: 0;
}

.pdf-tools-sidebar .card {
  position: sticky;
  top: 1rem;
  background: #ffffff;
  border: 1px solid var(--surface-border, #e0e0e0);
  border-radius: 12px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.06);
  padding: 1rem 1rem 0.5rem;
}

.pdf-tools-sidebar h3 {
  margin: 0 0 0.75rem;
  font-size: 1.1rem;
  font-weight: 700;
  color: var(--text-color, #222);
}

/* Sidebar form layout (replaces p-fluid) */
.sidebar-form {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.sidebar-form :deep(.p-button),
.sidebar-form :deep(.p-fileupload) {
  width: 100%;
}

/* Form fields (replaces p-field) */
.form-field {
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
}

.form-field--spaced {
  margin-top: 1rem;
}

.form-label {
  display: block;
  margin-bottom: 0.25rem;
  font-weight: 500;
  font-size: 0.875rem;
}

.form-hint {
  color: var(--p-text-muted-color, #6b7280);
  font-size: 0.8rem;
}

/* Buttons spacing helpers (replaces mt-2, mt-auto, etc.) */
.btn-mt {
  margin-top: 0.5rem;
}

.btn-mt-sm {
  margin-top: 0.25rem;
}

.btn-mt-auto {
  margin-top: auto;
}

.btn-full {
  width: 100%;
}

/* Insert buttons stack (replaces flex flex-column gap-2) */
.insert-buttons {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

/* Watermark grid (replaces grid + col-6) */
.watermark-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.5rem;
  margin-top: 0.5rem;
}

/* Page grid (replaces PrimeFlex grid + col-3) */
.page-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 0.5rem;
}

.page-grid-item {
  min-width: 0;
}

/* Input full width (replaces w-full) */
.input-full {
  width: 100%;
}

/* Fields and labels */
.pdf-tools-sidebar :deep(.p-inputtext),
.pdf-tools-sidebar :deep(.p-inputnumber-input) {
  width: 100%;
}

/* Page thumbnails */
.page-thumbnail {
  position: relative;
  border: 2px solid #ccc;
  border-radius: 10px;
  margin-bottom: 10px;
  text-align: center;
  cursor: pointer;
  transition: all 0.2s;
  overflow: hidden;
  background-color: #f8f9fa;
  height: 150px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.page-thumbnail.selected {
  border-color: var(--primary-color);
  transform: scale(1.05);
  box-shadow: 0 0 0 3px rgba(48, 79, 254, 0.25);
}

.page-thumbnail:hover {
  border-color: #a0aec0;
}

.page-thumbnail:hover .page-actions {
  opacity: 1;
}

.page-actions {
  position: absolute;
  top: 5px;
  right: 5px;
  z-index: 10;
  opacity: 0;
  transition: opacity 0.2s;
}

.page-actions :deep(.p-button) {
  background: rgba(255, 255, 255, 0.9);
  border: 1px solid #e5e7eb;
}

.page-selection {
  position: absolute;
  top: 5px;
  left: 5px;
  z-index: 10;
}

.page-number {
  position: absolute;
  bottom: 5px;
  right: 5px;
  background-color: rgba(0, 0, 0, 0.5);
  color: white;
  padding: 2px 5px;
  border-radius: 3px;
  font-size: 0.8rem;
  font-weight: 600;
}

.pdf-preview-container {
  flex-grow: 1;
}

.thumbnail-content {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.page-thumbnail :deep(canvas) {
  max-width: 100%;
  max-height: 100%;
  height: auto;
  width: auto;
  pointer-events: none;
}

.ghost {
  opacity: 0.5;
  background: #c8ebfb;
}

/* Dialog shared styles */
.dialog-header {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.dialog-footer-buttons {
  display: flex;
  gap: 0.5rem;
}

/* Page editor dialog */
:deep(.page-editor-dialog) {
  height: 85vh;
}

.page-editor-toolbar {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid var(--surface-border, #e0e0e0);
}

.page-editor-toolbar .spacer {
  flex: 1;
}

.zoom-indicator {
  color: #6b7280;
}

/* Editor layout (replaces flex on editor body) */
.editor-layout {
  display: flex;
}

.editor-footer {
  display: flex;
  justify-content: space-between;
  width: 100%;
}

/* Tool group */
.tool-group {
  position: sticky;
  top: 1rem;
  background: #ffffff;
  border: 1px solid var(--surface-border, #e0e0e0);
  border-radius: 12px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.06);
  padding: 1rem 1rem 0.5rem;
  width: 12rem;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
}

.tool-group :deep(.p-button),
.tool-group :deep(.p-fileupload) {
  width: 100%;
}

.tool-group :deep(.p-button.active),
.tool-group :deep(.p-fileupload.active) {
  background: #e0f2fe;
  border-color: #7dd3fc;
  color: #075985;
  box-shadow: 0 0 0 2px rgba(125, 211, 252, 0.35) inset;
}

.tool-group :deep(.p-button.active .p-button-icon) {
  color: #075985;
}

/* Text tool settings (replaces mt-2 container) */
.text-settings {
  margin-top: 0.5rem;
}

/* Page editor canvas */
.page-editor-canvas {
  height: calc(85vh - 130px);
  display: flex;
  align-items: flex-start;
  justify-content: center;
  overflow: auto;
  background: #f8fafc;
  border-radius: 8px;
  scroll-behavior: smooth;
  flex: 1;
}

.page-editor-canvas :deep(canvas) {
  position: relative;
}

.pdf-canvas-wrapper {
  position: relative;
}

.pdf-canvas-absolute {
  position: absolute;
}

.overlay-canvas {
  position: absolute;
  inset: 0;
  pointer-events: auto;
  cursor: crosshair;
  top: 0;
  left: 0;
}

/* Signature pad */
.signature-pad {
  width: 100%;
}

.signature-canvas {
  width: 100%;
  height: 180px;
  border: 1px dashed var(--surface-border, #e5e7eb);
  background: #fff;
}

/* Search input group (replaces p-inputgroup) */
.search-input-group {
  display: flex;
  margin-bottom: 0.5rem;
}

.search-input-group :deep(.p-inputtext) {
  flex: 1;
  border-top-right-radius: 0;
  border-bottom-right-radius: 0;
}

.search-input-group :deep(.p-button) {
  border-top-left-radius: 0;
  border-bottom-left-radius: 0;
}

/* Inline status (replaces flex align-items-center gap-2) */
.inline-status {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

/* Text utilities (replaces PrimeFlex utility classes) */
.text-muted {
  color: var(--p-text-muted-color, #6b7280);
}

.text-ellipsis {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.preview-icon {
  font-size: 3.5rem;
  color: var(--p-text-muted-color, #6b7280);
}

/* Item content in favorites/search lists (replaces flex align-items-center gap-2 flex-grow-1) */
.item-content {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-grow: 1;
  min-width: 0;
}

/* Spinner sizes */
.spinner-sm {
  width: 1.5rem;
  height: 1.5rem;
}

.spinner-md {
  width: 3rem;
  height: 3rem;
}

/* Favorites layout */
.favorites-layout {
  display: flex;
  gap: 1rem;
  height: 400px;
}

.favorites-list {
  flex: 0 0 300px;
  overflow-y: auto;
  border: 1px solid var(--surface-border, #e0e0e0);
  border-radius: 8px;
  padding: 0.5rem;
}

.favorite-item {
  display: flex;
  align-items: center;
  padding: 0.75rem;
  border-radius: 6px;
  cursor: pointer;
  transition: background-color 0.2s;
  margin-bottom: 0.25rem;
}

.favorite-item:hover {
  background-color: var(--surface-hover, #f5f5f5);
}

.favorite-item-selected {
  background-color: var(--primary-50, #e3f2fd);
  border: 1px solid var(--primary-200, #90caf9);
}

.favorites-preview {
  flex: 1;
  display: flex;
  flex-direction: column;
  border: 1px solid var(--surface-border, #e0e0e0);
  border-radius: 8px;
  background-color: var(--surface-50, #fafafa);
  overflow: hidden;
}

.preview-wrapper {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
}

.preview-controls {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.5rem;
  border-bottom: 1px solid var(--surface-border, #e0e0e0);
  background-color: white;
}

.preview-controls .page-info {
  min-width: 80px;
  text-align: center;
  font-weight: 500;
  color: var(--text-color, #333);
}

.preview-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  height: 100%;
}

.preview-content {
  flex: 1;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  overflow: auto;
  padding: 1rem;
}

.preview-content :deep(canvas) {
  max-width: 100%;
  height: auto;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

/* Insert dialog footer */
.insert-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  width: 100%;
}

.insert-footer-info {
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  margin-right: 0.5rem;
  min-width: 0;
}

.selected-info {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  color: var(--p-text-muted-color, #6b7280);
}

.selected-label {
  flex-shrink: 0;
  color: var(--p-text-muted-color, #6b7280);
}

.selected-name {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-weight: 500;
  flex-grow: 1;
}

.error-msg {
  color: var(--p-red-500, #ef4444);
  font-size: 0.875rem;
}

.insert-footer-actions {
  display: flex;
  gap: 0.5rem;
  margin-left: auto;
}

/* Dialog sizes */
.dialog-page-editor {
  width: 80vw;
  max-width: 1200px;
}

.dialog-signature-pad {
  width: 480px;
}

.dialog-insert-pdf {
  width: 60rem;
}
</style>
